import { Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton, Button, Box, Input, RadioGroup, Radio, Text, Divider, Checkbox, Flex, Avatar, InputGroup, InputLeftElement, Grid, GridItem, Tabs, TabList, Tab, Badge, HStack, VStack, Icon, Spinner, } from "@chakra-ui/react";
import { ArrowForwardIcon, ArrowBackIcon, SearchIcon, CheckIcon } from "@chakra-ui/icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";
import { AlertCard, CustomToast } from "../../../../components";
import { baseUrl, useFetch } from "../../../../hooks/useFetch";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { HrlenseInput } from "../../../../components/Input/Input";
import { AlertConfig, EmptyMeetingForm, MeetingForm, meetingSchema, Participant } from "./modal";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    reloadMeetings: () => void;
}

const StepIndicator = ({ step }: { step: 1 | 2 | 3 }) => {
    const steps = [
        { label: "Meeting details" },
        { label: "Participants" },
        { label: "Availability" },
    ];
    return (
        <HStack justify="center" spacing={0} mt={3}>
            {steps.map((s, i) => {
                const num = i + 1;
                const isDone = num < step;
                const isActive = num === step;
                return (
                    <HStack key={num} spacing={0}>
                        <VStack spacing={1}>
                            <Flex w="26px" h="26px" borderRadius="full" align="center" justify="center" border="1.5px solid" borderColor={isDone ? "green.500" : isActive ? "green.600" : "gray.300"} bg={isActive ? "green.600" : isDone ? "green.50" : "white"} color={isActive ? "white" : isDone ? "green.600" : "gray.400"} fontSize="11px" fontWeight="600">
                                {isDone ? <CheckIcon boxSize="10px" /> : num}
                            </Flex>
                            <Text fontSize="10px" fontWeight={isActive ? "600" : "400"} color={isActive ? "green.600" : isDone ? "green.500" : "gray.400"} whiteSpace="nowrap" >
                                {s.label}
                            </Text>
                        </VStack>
                        {i < 2 && (
                            <Box w="48px" h="1.5px" mb={4} mx={1} bg={isDone ? "green.400" : "gray.200"} />
                        )}
                    </HStack>
                );
            })}
        </HStack>
    );
};

const ModePill = ({ value, selected, onClick, }: { value: string; selected: boolean; onClick: () => void }) => (
    <Box as="button" type="button" px={5} py={2} borderRadius="full" border="1.5px solid" borderColor={selected ? "green.600" : "gray.200"} bg={selected ? "green.50" : "white"} color={selected ? "green.700" : "gray.500"} fontWeight={selected ? "600" : "400"} fontSize="sm" cursor="pointer" transition="all 0.15s" _hover={{ borderColor: "green.400", bg: "green.50" }} onClick={onClick} >
        {value}
    </Box>
);

const ParticipantRow = ({ item, checked, onToggle, }: { item: Participant; checked: boolean; onToggle: () => void }) => (
    <Flex align="center" gap={3} px={3} py="9px" borderBottom="0.5px solid" borderColor="gray.100" bg={checked ? "green.50" : "white"} cursor="pointer" onClick={onToggle} transition="background 0.1s" _hover={{ bg: checked ? "green.50" : "gray.50" }} _last={{ borderBottom: "none" }} >
        <Avatar size="sm" name={item.displayName} bg="blue.100" color="blue.700" />
        <Box flex={1}>
            <Text fontSize="sm" fontWeight="500" color="gray.800">{item.displayName}</Text>
            <Text fontSize="xs" color="gray.400">{item.role}</Text>
        </Box>
        <Checkbox isChecked={checked} onChange={onToggle} colorScheme="green" onClick={(e) => e.stopPropagation()} />
    </Flex>
);

const CreateMeetingModal = ({ isOpen, onClose, reloadMeetings }: Props) => {
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState<1 | 2 | 3>(1);
    const { addToast } = CustomToast();
    const { fetchApi } = useFetch(addToast);

    const [search, setSearch] = useState("");
    const [employees, setEmployees] = useState<Participant[]>([]);
    const [teamLeads, setTeamLeads] = useState<Participant[]>([]);
    const [availableUsers, setAvailableUsers] = useState<Participant[]>([]);
    const [conflictUsers, setConflictUsers] = useState<Participant[]>([]);
    const [alertConfig, setAlertConfig] = useState<AlertConfig | null>(null);
    const [loadingState, setLoadingState] = useState(false);
    const [selectedParticipants, setSelectedParticipants] = useState<{ id: number; role: string }[]>([]);
    const [selectedAvailable, setSelectedAvailable] = useState<number[]>([]);
    const [selectedConflict, setSelectedConflict] = useState<number[]>([]);
    const [participantsLoading, setParticipantsLoading] = useState(false);
    const [participantTab, setParticipantTab] = useState<"Employee" | "Team" | "Mixed">("Employee");

    const { handleSubmit, control, formState: { errors }, reset, watch, trigger, getValues, setValue } = useForm<MeetingForm>({
        resolver: yupResolver(meetingSchema as any),
        defaultValues: EmptyMeetingForm,
        mode: "onBlur",
    });

    const mode = watch("mode");
    const today = new Date().toLocaleDateString("en-CA");

    const resetModal = useCallback(() => {
        setStep(1);
        setSearch("");
        setEmployees([]);
        setTeamLeads([]);
        setSelectedParticipants([]);
        setAvailableUsers([]);
        setConflictUsers([]);
        setSelectedAvailable([]);
        setSelectedConflict([]);
        setAlertConfig(null);
        setParticipantTab("Employee");
        reset(EmptyMeetingForm);
    }, [reset]);

    const STEP_1_FIELDS: (keyof MeetingForm)[] = [
        "title", "description", "mode", "roomNo", "startTime", "endTime", "date",
    ];

    const loadParticipants = useCallback(async (type: "Employee" | "Team" | "Mixed", searchTerm = "",) => {
        setParticipantsLoading(true);
        const params = new URLSearchParams();
        params.append("participantType", type);
        if (searchTerm) params.append("search", searchTerm);

        const response = await fetchApi(`Meetings/participantList?${params.toString()}`, "GET");
        if (!response) { setParticipantsLoading(false); return; }

        if (type === "Employee") {
            setEmployees(response);
            setTeamLeads([]);
        } else if (type === "Team") {
            setTeamLeads(response);
            setEmployees([]);
        } else {
            setEmployees(Array.isArray(response) ? response : (response.employees ?? []));
            setTeamLeads(Array.isArray(response) ? [] : (response.teamLeads ?? []));
        }
        setParticipantsLoading(false);
    }, [fetchApi]);

    useEffect(() => {
        const delay = setTimeout(() => {
            if (step === 2) loadParticipants(participantTab, search);
        }, search ? 400 : 0);
        return () => clearTimeout(delay);
    }, [participantTab, search, step]);

    const listData = useMemo(() => {
        if (participantTab === "Employee") return employees;
        if (participantTab === "Team") return teamLeads;
        return [...employees, ...teamLeads];
    }, [participantTab, employees, teamLeads]);

    const isAllSelected = useMemo(() =>
        listData.length > 0 && listData.every(item =>
            selectedParticipants.some(p => p.id === item.id && p.role === item.role)
        ), [listData, selectedParticipants]);

    const isSelected = useCallback((item: Participant) =>
        selectedParticipants.some(p => p.id === item.id && p.role === item.role),
        [selectedParticipants]);

    const toggleSelect = useCallback((item: Participant) => {
        setSelectedParticipants(prev => {
            const exists = prev.some(p => p.id === item.id && p.role === item.role);
            return exists
                ? prev.filter(p => !(p.id === item.id && p.role === item.role))
                : [...prev, { id: item.id, role: item.role }];
        });
    }, []);

    const handleSelectAll = useCallback((checked: boolean) => {
        if (checked) {
            const next = listData.map(item => ({ id: item.id, role: item.role }));
            setSelectedParticipants(prev => {
                const combined = [...prev, ...next];
                return combined.filter((v, i, arr) =>
                    arr.findIndex(x => x.id === v.id && x.role === v.role) === i
                );
            });
        } else {
            setSelectedParticipants(prev =>
                prev.filter(p => !listData.some(item => item.id === p.id && item.role === p.role))
            );
        }
    }, [listData]);

    useEffect(() => {
        if (step !== 3) return;
        const check = async () => {
            setLoading(true);
            const timeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const payload = {
                participants: selectedParticipants,
                meetingDate: watch("date"),
                startTime: `${getValues("startTime")}:00`,
                endTime: `${getValues("endTime")}:00`,
                timeZoneId,
            };
            const response = await fetchApi("Meetings/checkAvailability", "POST", payload);
            if (response) {
                setAvailableUsers(response.available ?? []);
                setConflictUsers(response.conflict ?? []);
                setSelectedAvailable((response.available ?? []).map((u: Participant) => u.id));
                setSelectedConflict((response.conflict ?? []).map((u: Participant) => u.id));
            }
            setLoading(false);
        };
        check();
    }, [step]);

    const submitMeeting = async (
        formValues: MeetingForm,
        participantIds: number[]
    ) => {
        try {
            setLoadingState(true);

            const timeZoneId =
                Intl.DateTimeFormat().resolvedOptions().timeZone;

            const payload = {
                ...formValues,
                selectedParticipants: participantIds,
                timeZoneId
            };

            // Save payload so we can retry after Google authorization
            sessionStorage.setItem(
                "PendingMeeting",
                JSON.stringify(payload)
            );

            const loginData = JSON.parse(
                sessionStorage.getItem("loginData") || "{}"
            );

            const response = await fetch(
                `${baseUrl}Meetings/Meeting`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${loginData.token}`
                    },
                    body: JSON.stringify(payload)
                }
            );

            const result = await response.json();

            // Google authorization required
            if (
                response.status === 428 &&
                result.code === "GOOGLE_AUTH_REQUIRED"
            ) {
                const googleResponse = await fetch(
                    `${baseUrl}GoogleAuth/connect`,
                    {
                        method: "GET",
                        headers: {
                            Authorization: `Bearer ${loginData.token}`
                        }
                    }
                );

                const googleResult = await googleResponse.json();

                window.open(googleResult.url, "_blank", "width=600,height=700");

                return;
            }

            if (!response.ok) {
                throw new Error(result.message || "Something went wrong");
            }

            sessionStorage.removeItem("PendingMeeting");
            addToast({ message: result.message, status: "success" });
            setAlertConfig(null);
            resetModal();
            reloadMeetings();
            onClose();
        }
        catch (error: any) {
            addToast({ message: error.message || "Something went wrong", status: "error" });
        }
        finally {
            setLoadingState(false);
        }
    };


    useEffect(() => {

        const receiveMessage = async (event: MessageEvent) => {

            if (event.data?.type !== "GOOGLE_CONNECTED")
                return;

            const pendingMeeting = sessionStorage.getItem("PendingMeeting");

            if (!pendingMeeting)
                return;

            const loginData = JSON.parse(sessionStorage.getItem("loginData") || "{}");

            try {

                const response = await fetch(
                    `${baseUrl}Meetings/Meeting`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${loginData.token}`
                        },
                        body: pendingMeeting
                    }
                );

                const result = await response.json();

                if (!response.ok)
                    throw new Error(result.message);

                sessionStorage.removeItem("PendingMeeting");

                addToast({
                    message: result.message,
                    status: "success"
                });

                setAlertConfig(null);
                resetModal();
                reloadMeetings();
                onClose();

            }
            catch (err: any) {

                addToast({
                    message: err.message,
                    status: "error"
                });

            }

        };

        window.addEventListener("message", receiveMessage);

        return () => {
            window.removeEventListener("message", receiveMessage);
        };

    }, []);

    const handleConfirmStatusChange = handleSubmit((formValues) => {
        if (!alertConfig) return;
        const participantIds =
            alertConfig.type === "WITHOUT_CONFLICT"
                ? selectedAvailable
                : [...selectedAvailable, ...selectedConflict];
        submitMeeting(formValues, participantIds);
    });


    const handleNext = async () => {
        if (step === 1) {
            const valid = await trigger(STEP_1_FIELDS);
            if (!valid) { addToast({ message: "Please complete all required fields", status: "error" }); return; }
            setStep(2);
            loadParticipants(participantTab, "");
        } else if (step === 2) {
            if (selectedParticipants.length === 0) {
                addToast({ message: "Please select at least one participant", status: "error" });
                return;
            }
            setStep(3);
        }
    };

    const SkeletonRows = () => (
        <VStack spacing={0} align="stretch">
            {[...Array(5)].map((_, i) => (
                <Flex key={i} align="center" gap={3} px={3} py="9px" borderBottom="0.5px solid" borderColor="gray.100">
                    <Box w="32px" h="32px" borderRadius="full" bg="gray.100" />
                    <Box flex={1}>
                        <Box h="11px" bg="gray.100" borderRadius="md" w="55%" mb={1} />
                        <Box h="9px" bg="gray.50" borderRadius="md" w="35%" />
                    </Box>
                    <Box w="14px" h="14px" borderRadius="3px" bg="gray.100" />
                </Flex>
            ))}
        </VStack>
    );

    return (
        <>
            <Modal isOpen={isOpen} onClose={() => { resetModal(); onClose(); }} size="2xl" isCentered >
                <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(2px)" />
                <ModalContent borderRadius="14px" overflow="hidden">
                    <ModalHeader pb={0} pt={5}>
                        <Text textAlign="center" fontSize="md" fontWeight="600" color="gray.800">Create New Meeting</Text>
                        <ModalCloseButton top={4} right={4} />
                        <StepIndicator step={step} />
                    </ModalHeader>

                    <Divider mt={4} />

                    <ModalBody overflowY="auto" px={6} py={5}>
                        {step === 1 && (
                            <VStack spacing={4} align="stretch">
                                <HrlenseInput isRequired label="Title" name="title" control={control} placeholder="Enter meeting title" errors={errors} />
                                <Box>
                                    <Text mb={1} fontSize="sm" fontWeight="500" color="gray.600">Description <Text as="span" color="red.500">*</Text>
                                    </Text>
                                    <Controller name="description" control={control} render={({ field }) => (
                                        <SunEditor height="100px" setContents={field.value} onChange={field.onChange} setOptions={{ minHeight: "100px" }} />
                                    )}
                                    />
                                    {errors.description?.message && (<Text color="red.500" fontSize="xs" mt={1}>{errors.description.message}</Text>)}
                                </Box>

                                <Grid templateColumns="1.2fr 1fr 1fr" gap={3}>
                                    <Box>
                                        <Text mb={1} fontSize="sm" fontWeight="500" color="gray.600">
                                            Date <Text as="span" color="red.500">*</Text>
                                        </Text>
                                        <Controller name="date" control={control} render={({ field }) => (
                                            <Input type="date" {...field} min={today} size="sm" borderRadius="md" />
                                        )}
                                        />
                                        {errors.date?.message && (<Text color="red.500" fontSize="xs" mt={1}>{errors.date.message}</Text>)}
                                    </Box>

                                    <Controller name="startTime" control={control} render={({ field }) => (
                                        <Box>
                                            <Text mb={1} fontSize="sm" fontWeight="500" color="gray.600">Start time</Text>
                                            <Input type="time" size="sm" borderRadius="md" value={field.value || ""}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    const meetingDate = watch("date");
                                                    if (!meetingDate) { field.onChange(value); return; }
                                                    const now = new Date();
                                                    const [y, m, d] = meetingDate.split("-").map(Number);
                                                    const [h, min] = value.split(":").map(Number);
                                                    const selected = new Date(y, m - 1, d, h, min);
                                                    if (selected < now) {
                                                        addToast({ message: "Past time not allowed", status: "error" });
                                                        return;
                                                    }
                                                    field.onChange(value);
                                                }}
                                            />
                                            {errors.startTime && (<Text color="red.500" fontSize="xs" mt={1}>{errors.startTime.message}</Text>)}
                                        </Box>
                                    )}
                                    />

                                    <Controller name="endTime" control={control} render={({ field }) => (
                                        <Box>
                                            <Text mb={1} fontSize="sm" fontWeight="500" color="gray.600">End time</Text>
                                            <Input type="time" size="sm" borderRadius="md" {...field} />
                                            {errors.endTime && (<Text color="red.500" fontSize="xs" mt={1}>{errors.endTime.message}</Text>)}
                                        </Box>
                                    )}
                                    />
                                </Grid>

                                {/* Mode */}
                                <Box>
                                    <Text mb={2} fontSize="sm" fontWeight="500" color="gray.600">Mode</Text>
                                    <Controller name="mode" control={control} render={({ field }) => (
                                        <HStack spacing={2}>
                                            <ModePill value="Online" selected={field.value === "Online"} onClick={() => field.onChange("Online")} />
                                            <ModePill value="Offline" selected={field.value === "Offline"} onClick={() => field.onChange("Offline")} />
                                        </HStack>
                                    )}
                                    />
                                </Box>

                                {mode === "Online" && (
                                    <Box>
                                        <Text mb={2} fontSize="sm" fontWeight="500" color="gray.600">Platform</Text>
                                        <Controller name="platform" control={control} render={({ field }) => (
                                            <RadioGroup {...field}>
                                                <HStack>
                                                    <Radio value="GMEET" colorScheme="green">Google Meet</Radio>
                                                </HStack>
                                            </RadioGroup>
                                        )}
                                        />
                                        {errors.platform && (<Text color="red.500" fontSize="xs" mt={1}>{errors.platform.message}</Text>)}
                                    </Box>
                                )}

                                {mode === "Offline" && (
                                    <Controller name="roomNo" control={control} render={({ field }) => (
                                        <Box>
                                            <Text mb={1} fontSize="sm" fontWeight="500" color="gray.600">Meeting location</Text>
                                            <Input {...field} placeholder="e.g. Conference Room A, Floor 3" size="sm" borderRadius="md" />
                                            {errors.roomNo && (<Text color="red.500" fontSize="xs" mt={1}>{errors.roomNo.message}</Text>
                                            )}
                                        </Box>
                                    )}
                                    />
                                )}
                            </VStack>
                        )}

                        {step === 2 && (
                            <VStack spacing={3} align="stretch">
                                {/* Tab bar */}
                                <Tabs index={["Employee", "Team", "Mixed"].indexOf(participantTab)} onChange={(i) => {
                                    const types = ["Employee", "Team", "Mixed"] as const;
                                    setParticipantTab(types[i]);
                                    setSearch("");
                                }}
                                    colorScheme="green"
                                    size="sm"
                                    variant="line"
                                >
                                    <TabList>
                                        <Tab fontWeight="500" fontSize="sm">Employee</Tab>
                                        <Tab fontWeight="500" fontSize="sm">Team Lead</Tab>
                                        <Tab fontWeight="500" fontSize="sm">Mixed</Tab>
                                    </TabList>
                                </Tabs>

                                <InputGroup size="sm">
                                    <InputLeftElement pointerEvents="none">
                                        <SearchIcon color="gray.400" />
                                    </InputLeftElement>
                                    <Input placeholder="Search by name..." value={search} onChange={(e) => setSearch(e.target.value)} borderRadius="md" />
                                </InputGroup>

                                <Flex align="center" justify="space-between">
                                    <Text fontSize="xs" color="gray.500">
                                        {selectedParticipants.length > 0 ? `${selectedParticipants.length} selected` : "No participants selected yet"}
                                    </Text>
                                    <Checkbox isChecked={isAllSelected} onChange={(e) => handleSelectAll(e.target.checked)} colorScheme="green" size="sm" >
                                        <Text fontSize="xs" color="gray.500">Select all</Text>
                                    </Checkbox>
                                </Flex>

                                <Box border="0.5px solid" borderColor="gray.200" borderRadius="lg" overflow="hidden" maxH="320px" overflowY="auto">
                                    {participantsLoading ? (
                                        <SkeletonRows />
                                    ) : listData.length === 0 ? (
                                        <Flex align="center" justify="center" py={8}>
                                            <Text fontSize="sm" color="gray.400">No results found</Text>
                                        </Flex>
                                    ) : (
                                        listData.map((item, idx) => (
                                            <ParticipantRow key={`${item.id}-${item.role}-${idx}`} item={item} checked={isSelected(item)} onToggle={() => toggleSelect(item)} />
                                        ))
                                    )}
                                </Box>
                            </VStack>
                        )}

                        {step === 3 && (
                            <Box>
                                {loading ? (
                                    <Flex align="center" justify="center" py={10} gap={3} direction="column">
                                        <Spinner color="green.600" size="md" />
                                        <Text fontSize="sm" color="gray.500">Checking availability...</Text>
                                    </Flex>
                                ) : (
                                    <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                                        {/* Available */}
                                        <GridItem>
                                            <Flex align="center" gap={2} mb={2}>
                                                <Box w="8px" h="8px" borderRadius="full" bg="green.500" />
                                                <Text fontSize="sm" fontWeight="600" color="gray.700">
                                                    {availableUsers.length} Available
                                                </Text>
                                            </Flex>
                                            <Box border="0.5px solid" borderColor="gray.200" borderRadius="lg" overflow="hidden" maxH="360px" overflowY="auto">
                                                {availableUsers.length === 0 ? (
                                                    <Flex py={6} justify="center">
                                                        <Text fontSize="sm" color="gray.400">No available participants</Text>
                                                    </Flex>
                                                ) : availableUsers.map((u) => (
                                                    <Flex key={u.id} align="center" gap={3} px={3} py="9px"
                                                        borderBottom="0.5px solid" borderColor="gray.100"
                                                        _last={{ borderBottom: "none" }}
                                                        bg={selectedAvailable.includes(u.id) ? "green.50" : "white"}
                                                        cursor="pointer"
                                                        onClick={() => setSelectedAvailable(prev =>
                                                            prev.includes(u.id) ? prev.filter(id => id !== u.id) : [...prev, u.id]
                                                        )}
                                                    >
                                                        <Avatar size="sm" name={u.name} bg="green.100" color="green.700" />
                                                        <Box flex={1}>
                                                            <Text fontSize="sm" fontWeight="500">{u.name}</Text>
                                                            <Text fontSize="xs" color="gray.400">{u.role}</Text>
                                                        </Box>
                                                        <Checkbox
                                                            isChecked={selectedAvailable.includes(u.id)}
                                                            pointerEvents="none"
                                                            colorScheme="green"
                                                        />
                                                    </Flex>
                                                ))}
                                            </Box>
                                        </GridItem>

                                        <GridItem>
                                            <Flex align="center" gap={2} mb={2}>
                                                <Box w="8px" h="8px" borderRadius="full" bg="red.400" />
                                                <Text fontSize="sm" fontWeight="600" color="gray.700">
                                                    {conflictUsers.length} Conflict
                                                </Text>
                                            </Flex>
                                            <Box border="0.5px solid" borderColor="gray.200" borderRadius="lg" overflow="hidden" maxH="360px" overflowY="auto">
                                                {conflictUsers.length === 0 ? (
                                                    <Flex py={6} justify="center">
                                                        <Text fontSize="sm" color="gray.400">No conflicts</Text>
                                                    </Flex>
                                                ) : conflictUsers.map((u) => (
                                                    <Flex key={u.id} align="center" gap={3} px={3} py="9px" borderBottom="0.5px solid" borderColor="gray.100" _last={{ borderBottom: "none" }}
                                                        bg={selectedConflict.includes(u.id) ? "red.50" : "white"}
                                                        cursor="pointer"
                                                        onClick={() => setSelectedConflict(prev =>
                                                            prev.includes(u.id) ? prev.filter(id => id !== u.id) : [...prev, u.id]
                                                        )}
                                                    >
                                                        <Avatar size="sm" name={u.name} bg="red.100" color="red.600" />
                                                        <Box flex={1}>
                                                            <Text fontSize="sm" fontWeight="500">{u.name}</Text>
                                                            <Text fontSize="xs" color="gray.400">{u.role}</Text>
                                                        </Box>
                                                        <Checkbox
                                                            isChecked={selectedConflict.includes(u.id)}
                                                            pointerEvents="none"
                                                            colorScheme="red"
                                                        />
                                                    </Flex>
                                                ))}
                                            </Box>
                                        </GridItem>
                                    </Grid>
                                )}
                            </Box>
                        )}
                    </ModalBody>

                    <Divider />
                    <ModalFooter justifyContent="space-between" px={6} py={4} gap={3}>
                        <Box>
                            {step > 1 && (
                                <Button variant="ghost" size="sm" leftIcon={<ArrowBackIcon />} onClick={() => setStep((s) => (s - 1) as 1 | 2 | 3)} color="gray.600" > Back </Button>
                            )}
                        </Box>

                        <HStack spacing={2}>
                            {step < 3 && (
                                <Button bg="#006838" color="white" size="sm" _hover={{ bg: "#005529" }} rightIcon={<ArrowForwardIcon />} onClick={handleNext} >
                                    {step === 2 ? "Check availability" : "Next"}
                                </Button>
                            )}

                            {step === 3 && !loading && (
                                <>
                                    {conflictUsers.length === 0 ? (
                                        <Button bg="#006838" color="white" size="sm" _hover={{ bg: "#005529" }} onClick={() =>
                                            setAlertConfig({
                                                type: "ANYWAY",
                                                description: "All participants are available. Do you want to create this meeting?",
                                                colorScheme: "green",
                                            })
                                        }
                                        >
                                            Create meeting
                                        </Button>
                                    ) : (
                                        <>
                                            <Button variant="outline" size="sm" colorScheme="green" onClick={() => {
                                                if (availableUsers.length === 0) {
                                                    addToast({ message: "No available participants to process", status: "warning" });
                                                    return;
                                                }
                                                const excluded = availableUsers.filter(u => !selectedAvailable.includes(u.id)).length + conflictUsers.length;
                                                setAlertConfig({ type: "WITHOUT_CONFLICT", description: `${excluded} participant(s) with conflicts will be excluded. Continue?`, colorScheme: "green", });
                                            }}
                                            >
                                                Skip conflicts
                                            </Button>
                                            <Button colorScheme="red" size="sm"
                                                onClick={() => {
                                                    const excluded = conflictUsers.filter(u => !selectedConflict.includes(u.id)).length;
                                                    setAlertConfig({ type: "ANYWAY", description: excluded > 0 ? `${selectedConflict.length} conflict participant(s) included, ${excluded} excluded. Continue?` : `${conflictUsers.length} participant(s) have conflicts but will be included. Continue anyway?`, colorScheme: "red", });
                                                }}
                                            >
                                                Include all
                                            </Button>
                                        </>
                                    )}
                                </>
                            )}
                        </HStack>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <AlertCard
                isOpen={!!alertConfig}
                isLoading={loadingState}
                onClose={() => { if (!loadingState) setAlertConfig(null); }}
                onConfirm={handleConfirmStatusChange}
                description={alertConfig?.description || ""}
                colorScheme={alertConfig?.colorScheme || "green"}
            />
        </>
    );
};

export default CreateMeetingModal;